import { createComponent } from '@ycos/fela'
import { createFontStyle, getFontStyles } from '../'

const getHeadingMargin = (spacingMultiplier) => `0 0 ${spacingMultiplier}px`

export const PageHeading = createFontStyle('PageHeading', { type: 'Heading', tagName: 'h1', name: 'B1' }, ({ textAlign, inverseColor, theme }) => {
  const {
    spacingMultiplier,
    custom: { heading }
  } = theme

  return {
    color: inverseColor ? heading.inverseColor : heading.color,
    margin: getHeadingMargin(spacingMultiplier),
    textAlign,
    direction: 'ltr'
  }
})

export const FooterHeading = createComponent(
  'FooterHeading',
  ({ textAlign, inverseColor, theme }) => {
    const {
      typography,
      spacingMultiplier,
      custom: { heading, footerHeading }
    } = theme

    return {
      ...getFontStyles(typography, { type: 'Heading', name: footerHeading.name }),
      color: inverseColor ? heading.inverseColor : heading.color,
      margin: getHeadingMargin(spacingMultiplier),
      textAlign,
      direction: 'ltr'
    }
  },
  'h3'
)

export const HeadingJumbo = createFontStyle('HeadingJumbo', { type: 'Heading', tagName: 'h2', name: 'B0' }, ({ textAlign, inverseColor, theme }) => {
  const {
    spacingMultiplier,
    custom: { heading }
  } = theme

  return {
    color: inverseColor ? heading.inverseColor : heading.color,
    margin: getHeadingMargin(spacingMultiplier),
    textAlign,
    direction: 'ltr'
  }
})

export const HeadingLarge = createFontStyle(
  'HeadingLarge',
  { type: 'Heading', tagName: 'h2', name: 'B2' },
  ({ textAlign, inverseColor, theme, isLoyaltyStyle }) => {
    const {
      spacingMultiplier,
      custom: { heading, headingLargeMargin }
    } = theme

    return {
      color: inverseColor ? heading.inverseColor : heading.color,
      margin: headingLargeMargin || getHeadingMargin(spacingMultiplier),
      textAlign,
      direction: 'ltr',
      ...(isLoyaltyStyle && heading.loyaltyFontStyle)
    }
  }
)

export const HeadingLargeSackers = createFontStyle(
  'HeadingLargeSackers',
  { type: 'Heading', tagName: 'h2', name: 'A7' },
  ({ textAlign, inverseColor, theme }) => {
    const {
      spacingMultiplier,
      custom: { heading, headingLargeMargin }
    } = theme

    return {
      color: inverseColor ? heading.inverseColor : heading.color,
      margin: headingLargeMargin || getHeadingMargin(spacingMultiplier),
      textAlign,
      direction: 'ltr'
    }
  }
)

export const HeadingMedium = createFontStyle('HeadingMedium', { type: 'Heading', tagName: 'h2', name: 'B3' }, ({ textAlign, inverseColor, theme }) => {
  const {
    spacingMultiplier,
    custom: { heading, headingMediumMargin }
  } = theme

  return {
    color: inverseColor ? heading.inverseColor : heading.color,
    margin: headingMediumMargin || getHeadingMargin(spacingMultiplier),
    textAlign,
    direction: 'ltr'
  }
})

export const HeadingMediumSackers = createFontStyle(
  'HeadingMediumSackers',
  { type: 'Heading', tagName: 'h2', name: 'A6' },
  ({ textAlign, inverseColor, theme, isLoyaltyStyle }) => {
    const {
      spacingMultiplier,
      custom: { heading, headingMediumMargin }
    } = theme

    return {
      color: inverseColor ? heading.inverseColor : heading.color,
      margin: headingMediumMargin || getHeadingMargin(spacingMultiplier),
      textAlign,
      direction: 'ltr',
      ...(isLoyaltyStyle && heading.loyaltyFontStyle)
    }
  }
)

export const HeadingSmall = createComponent(
  'HeadingSmall',
  ({ textAlign, inverseColor, theme, isLoyaltyStyle }) => {
    const {
      typography,
      spacingMultiplier,
      custom: { heading, featuredCardHeading, headingSmallMargin }
    } = theme

    return {
      ...getFontStyles(typography, { type: 'Heading', name: featuredCardHeading.name }),
      color: inverseColor ? heading.inverseColor : heading.color,
      margin: headingSmallMargin || getHeadingMargin(spacingMultiplier),
      textAlign,
      direction: 'ltr',
      ...(isLoyaltyStyle && heading.loyaltyFontStyle)
    }
  },
  'h2'
)

export const HeadingXSmall = createComponent(
  'HeadingXSmall',
  ({ textAlign, inverseColor, theme }) => {
    const {
      typography,
      spacingMultiplier,
      custom: { heading, titleXSmall }
    } = theme

    return {
      ...getFontStyles(typography, { type: 'Heading', name: titleXSmall.name }),
      color: inverseColor ? heading.inverseColor : heading.color,
      margin: titleXSmall.margin || getHeadingMargin(spacingMultiplier),
      textAlign,
      direction: 'ltr'
    }
  },
  'h2'
)

export const HeadingHeroBanner = createComponent(
  'HeadingHeroBanner',
  ({ textAlign, inverseColor, theme, isLoyaltyStyle }) => {
    const {
      typography,
      spacingMultiplier,
      custom: { heading, heroBannerTitle }
    } = theme

    return {
      ...getFontStyles(typography, { type: 'Heading', name: heroBannerTitle.name }),
      color: inverseColor ? heading.inverseColor : heading.color,
      margin: heroBannerTitle.margin || getHeadingMargin(spacingMultiplier),
      textAlign,
      direction: 'ltr',
      ...(isLoyaltyStyle && heroBannerTitle.loyaltyFontStyle)
    }
  },
  'h2'
)

export const HeadingLargeUppercase = createFontStyle(
  'HeadingLargeUppercase',
  { type: 'Heading', tagName: 'h2', name: 'A1' },
  ({ textAlign, inverseColor, theme }) => {
    const {
      spacingMultiplier,
      custom: { heading, headingLargeUppercaseMargin }
    } = theme

    return {
      color: inverseColor ? heading.inverseColor : heading.color,
      margin: headingLargeUppercaseMargin || getHeadingMargin(spacingMultiplier * 3),
      textAlign,
      direction: 'ltr'
    }
  }
)

export const SubtitleHeading = createFontStyle('SubtitleHeading', { type: 'Heading', tagName: 'h2', name: 'B1' }, ({ textAlign, inverseColor, theme }) => {
  const {
    custom: { heading }
  } = theme

  return {
    color: inverseColor ? heading.inverseColor : heading.color,
    margin: 0,
    textAlign,
    fontStyle: 'italic',
    direction: 'ltr'
  }
})
